import { useRef, useCallback, useState, useEffect } from 'react';
import useSWR from 'swr';
import { inspectionApi, storageApi } from '../api/client';
import { publicUrl } from '../../utils/backendConfig';
import { useTekMediaSync } from './useTekMediaSync';
import type {
  InspectionResponse,
  InspectionCategory,
  InspectionItemResponse,
  ItemPatchRequest,
} from '../types';

// `mediaSyncConversationId` opts in to the extension's background Tek media sync
// (per-photo send state + refetch as media lands). Pass it only from the view that
// renders the item photos: each subscriber re-renders per event and refetches.
export function useInspection(inspectionId: number | undefined, opts?: { mediaSyncConversationId?: number | null }) {
  const { data, error, isLoading, mutate } = useSWR<InspectionResponse>(
    inspectionId ? `vehicle/inspection/${inspectionId}` : null,
    () => inspectionApi.get(inspectionId!),
    {
      refreshInterval: (latestData) => {
        const isBusy = latestData?.items.some(i => !i.is_hidden && (i.state === 'analyzing' || i.state === 'uploaded'));
        return isBusy ? 2000 : 5000;
      },
    },
  );

  // Group items by category — template items first, extra items (from previous template) at end
  const categories: InspectionCategory[] = [];
  if (data?.items) {
    const map = new Map<string, InspectionCategory>();
    const extraMap = new Map<string, InspectionCategory>();
    const sorted = [...data.items].sort((a, b) => {
      // Extra items always sort after template items
      if (!!a.is_extra !== !!b.is_extra) return a.is_extra ? 1 : -1;
      const catDiff = (a.category_order ?? 0) - (b.category_order ?? 0);
      if (catDiff !== 0) return catDiff;
      return a.id - b.id;
    });
    for (const item of sorted) {
      const targetMap = item.is_extra ? extraMap : map;
      const key = item.category;
      if (!targetMap.has(key)) {
        targetMap.set(key, {
          id: item.category_id ?? null,
          name: item.category,
          display_name: item.category_display_name || item.category,
          order: item.category_order ?? 0,
          items: [],
          is_extra: !!item.is_extra,
        });
      }
      targetMap.get(key)!.items.push(item);
    }
    categories.push(...map.values(), ...extraMap.values());
  }

  // Revalidate as soon as background media lands (Tek photos copied in / an upload
  // to Tek finished) instead of on the next 2-5s poll. Debounced: a burst = one GET.
  const mediaRevalidateTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onMediaLanded = useCallback(() => {
    if (mediaRevalidateTimer.current) clearTimeout(mediaRevalidateTimer.current);
    mediaRevalidateTimer.current = setTimeout(() => {
      mediaRevalidateTimer.current = null;
      mutate();
    }, 300);
  }, [mutate]);
  useEffect(() => () => {
    if (mediaRevalidateTimer.current) clearTimeout(mediaRevalidateTimer.current);
  }, []);
  const tekMedia = useTekMediaSync(opts?.mediaSyncConversationId, onMediaLanded);

  const [uploadingItemIds, setUploadingItemIds] = useState<Set<number>>(new Set());

  // Per-item debounce timers — rapid clicks only fire one API call (last-write-wins)
  const patchTimers = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());

  const patchItem = useCallback((itemId: number, payload: ItemPatchRequest) => {
    if (!inspectionId) return;

    // 1. Apply optimistic update synchronously — no revalidation, no flicker
    mutate(
      (current): InspectionResponse => {
        if (!current) return current as unknown as InspectionResponse;
        return {
          ...current,
          items: current.items.map((item) =>
            item.id === itemId
              ? ({ ...item, ...payload } as InspectionItemResponse)
              : item,
          ),
        };
      },
      { revalidate: false },
    );

    // is_hidden changes: send immediately (no debounce) to avoid SWR refresh race
    if ('is_hidden' in payload) {
      (async () => {
        try {
          await inspectionApi.patchItem(itemId, payload);
          mutate();
        } catch {
          mutate();
        }
      })();
      return;
    }

    // 2. Debounce the actual API call — cancel previous pending call for this item
    const existing = patchTimers.current.get(itemId);
    if (existing) clearTimeout(existing);

    const timer = setTimeout(async () => {
      patchTimers.current.delete(itemId);
      try {
        await inspectionApi.patchItem(itemId, payload);
        // Sync with server after confirmed save
        mutate();
      } catch {
        // Restore server state on error
        mutate();
      }
    }, 300);

    patchTimers.current.set(itemId, timer);
  }, [inspectionId, mutate]);

  const addPhotos = async (itemId: number, files: File[]) => {
    // PATCH the new photos and request a re-analysis right after. The
    // backend writes any AI rewording to `description` (suggestion
    // store), never to `description_mechanic` (the canonical mechanic
    // text the customer signs against), so kicking off analysis can't
    // overwrite the mechanic's authored description.
    if (!inspectionId || files.length === 0) return;
    setUploadingItemIds((prev) => new Set(prev).add(itemId));
    try {
      const uploads = await Promise.all(
        files.map((f) => storageApi.uploadToPresignedUrl(f)),
      );
      const current = data?.items.find((it) => it.id === itemId);
      const existing = (current?.photos as unknown[] | undefined) ?? [];
      const newPhotos = uploads.map((u) => ({
        url: u.blob_name,
        label: 'primary',
        uploaded_at: new Date().toISOString(),
      }));
      // Optimistic: render new photos immediately, then patch + revalidate
      mutate(
        (cur): InspectionResponse => {
          if (!cur) return cur as unknown as InspectionResponse;
          return {
            ...cur,
            items: cur.items.map((it) =>
              it.id === itemId
                ? ({ ...it, photos: [...existing, ...newPhotos] } as InspectionItemResponse)
                : it,
            ),
          };
        },
        { revalidate: false },
      );
      // PATCH runs inline AI enhance. NOTE the response shape quirk: the
      // inspect/patch-with-AI result writes the suggestion to
      // item.description_autofill server-side but RETURNS it in the response's
      // `description` field (no separate description_autofill key on this one
      // shape — every other full-item response exposes both). So read
      // `description` here; keep `description_autofill` as a forward-compatible
      // fallback if the backend later adds the proper key. Pull it into the
      // cache so the suggestion card renders immediately, no extra GET.
      const resp = await inspectionApi.patchItem(itemId, ({ photos: [...existing, ...newPhotos], request_ai_analysis: true } as any));
      const enhancedDescription = ((resp as any)?.description_autofill ?? (resp as any)?.description) as string | undefined;
      if (enhancedDescription) {
        mutate(
          (cur): InspectionResponse => {
            if (!cur) return cur as unknown as InspectionResponse;
            return {
              ...cur,
              items: cur.items.map((it) =>
                it.id === itemId
                  ? ({ ...it, description_autofill: enhancedDescription } as InspectionItemResponse)
                  : it,
              ),
            };
          },
          { revalidate: false },
        );
      } else {
        mutate();
      }
    } finally {
      setUploadingItemIds((prev) => { const next = new Set(prev); next.delete(itemId); return next; });
    }
  };

  // Save an annotated photo IN PLACE: the edited blob becomes the canonical
  // `url`, and the pristine original is preserved under `original_url` (only on
  // the first edit). No new photo entry is appended — the UI toggles between the
  // edited (canonical) and original version. No AI re-analysis — an annotation
  // isn't a new finding.
  const editPhoto = async (itemId: number, photoIndex: number, file: File) => {
    if (!inspectionId) return;
    setUploadingItemIds((prev) => new Set(prev).add(itemId));
    try {
      const { blob_name } = await storageApi.uploadToPresignedUrl(file);
      const current = data?.items.find((it) => it.id === itemId);
      const existing = [...((current?.photos as Record<string, unknown>[] | undefined) ?? [])];
      const prev = existing[photoIndex];
      if (!prev) return;
      const entry = { ...prev } as Record<string, unknown>;
      if (!entry.original_url) entry.original_url = entry.url; // preserve pristine original once
      entry.url = blob_name;                  // edited becomes canonical
      entry.public_url = publicUrl(blob_name); // optimistic display URL (backend re-resolves on GET)
      entry.edited_at = new Date().toISOString();
      existing[photoIndex] = entry;
      const next = existing;
      mutate(
        (cur): InspectionResponse => {
          if (!cur) return cur as unknown as InspectionResponse;
          return {
            ...cur,
            items: cur.items.map((it) =>
              it.id === itemId ? ({ ...it, photos: next } as InspectionItemResponse) : it,
            ),
          };
        },
        { revalidate: false },
      );
      await inspectionApi.patchItem(itemId, ({ photos: next } as any));
      mutate();
    } finally {
      setUploadingItemIds((prev) => { const n = new Set(prev); n.delete(itemId); return n; });
    }
  };

  // Revert an annotated photo back to its pristine original IN PLACE: restore
  // `url` from `original_url`, then drop the `original_url`/`edited_at` markers
  // so the photo reads as un-edited again. Inverse of editPhoto. No-op if the
  // photo was never edited (no original_url to restore).
  const revertPhoto = async (itemId: number, photoIndex: number) => {
    if (!inspectionId) return;
    const current = data?.items.find((it) => it.id === itemId);
    const existing = [...((current?.photos as Record<string, unknown>[] | undefined) ?? [])];
    const prev = existing[photoIndex];
    if (!prev || !prev.original_url) return; // nothing to revert to
    setUploadingItemIds((p) => new Set(p).add(itemId));
    try {
      const entry = { ...prev } as Record<string, unknown>;
      const orig = entry.original_url as string;
      entry.url = orig;                  // original becomes canonical again
      entry.public_url = publicUrl(orig); // optimistic display URL (backend re-resolves on GET)
      delete entry.original_url;          // no longer an edited photo
      delete entry.edited_at;
      existing[photoIndex] = entry;
      const next = existing;
      mutate(
        (cur): InspectionResponse => {
          if (!cur) return cur as unknown as InspectionResponse;
          return {
            ...cur,
            items: cur.items.map((it) =>
              it.id === itemId ? ({ ...it, photos: next } as InspectionItemResponse) : it,
            ),
          };
        },
        { revalidate: false },
      );
      await inspectionApi.patchItem(itemId, ({ photos: next } as any));
      mutate();
    } finally {
      setUploadingItemIds((p) => { const n = new Set(p); n.delete(itemId); return n; });
    }
  };

  return {
    inspection: data,
    categories,
    error,
    isLoading,
    mutate,
    patchItem,
    addPhotos,
    editPhoto,
    revertPhoto,
    uploadingItemIds,
    tekMedia,
  };
}

export function useInspectionTemplates() {
  const { data, error, isLoading } = useSWR(
    'vehicle/inspection/templates',
    () => inspectionApi.getTemplates(),
  );
  return { templates: data ?? [], error, isLoading };
}
